/**
 * glowBlobs — static radial glows (floor/canopy bloom) anchored at normalized
 * positions in the scatter region. No animation. Migrated 1:1 from
 * AtmosphereLayer.buildGlowBlobs. A normalized blob in the sky band (atY ≤
 * SKY_BAND_MAX_AT_Y) is the bloom around a sun/moon and holds its place in the
 * view with it (skyAnchor.ts); floor and canopy glow stay put in the world.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { placeGlowBlobs } from '../atmospherePlacement';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { lerpColor } from '../../../../shared/utils/MathUtils';
import type { ISprite } from '../../../rendering/IRenderer';
import { anchorToSky, SKY_BAND_MAX_AT_Y, type SkyAnchored } from '../skyAnchor';

const buildGlowBlobs: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'glowBlobs') return null;
  const sprites: ISprite[] = [];
  // Base colours index-aligned with `sprites` — the tint driver lerps from these.
  const baseColors: number[] = [];
  const sky: SkyAnchored[] = [];
  const placed = placeGlowBlobs(layer.blobs, ctx.region);
  placed.forEach((g, i) => {
    const s = createGlowSprite({ x: g.x, y: g.y, size: g.size, color: g.color, alpha: g.alpha, textureKey: layer.textureKey, blendMode: layer.blendMode, parent: ctx.layerContainer });
    if (!s) return;
    sprites.push(s);
    baseColors.push(g.color);
    const spec = layer.blobs[i];
    if ('atY' in spec && typeof spec.atY === 'number' && spec.atY <= SKY_BAND_MAX_AT_Y) sky.push({ sprite: s, atX: spec.atX, atY: spec.atY });
  });
  const unsub = anchorToSky(ctx, sky);
  // Opacity is a container concern (AtmosphereLayer.container.alpha). A `tint`
  // modulator (+ the layer's `tintTo`) drives setTint: each blob lerps its
  // authored colour → tintTo over 0..1, so the bloom shifts hue as it breathes.
  const { tintTo } = layer;
  const setTint = tintTo === undefined ? undefined : (t: number) => {
    for (let i = 0; i < sprites.length; i += 1) sprites[i].setTint(lerpColor(baseColors[i], tintTo, t));
  };
  return makeContentHandle({ sprites, setTint, onDestroy: unsub });
};

registerEffect({ kind: 'glowBlobs', build: buildGlowBlobs, depth: Z_LAYERS.ATMOSPHERE });
